/**
 * GFG-270 — Reports: Redemptions. Pure helpers: the Blotter query state <-> API params translation,
 * status label round-trip, and the cell/CSV formatting the columns file consumes.
 */
import type { AppliedFilter } from "@/components/BlotterFilterBar";
import type { CampaignLinkStatus } from "@/constants/campaings";
import { CAMPAIGN_LINK_STATUS_VALUES, CAMPAIGN_STATUSES } from "@/constants/campaings";
import type { RedemptionSortKey } from "@/constants/reportsRedemptions";
import {
  COLUMN_TO_SORT_KEY,
  EMPTY_CELL,
  FILTER_FIELD_IDS,
  FIXED_COLUMN_SORT_KEY,
  SCHEDULED_LABEL,
} from "@/constants/reportsRedemptions";
import type { RedemptionColumnId, RedemptionsListParams } from "@/interfaces/reportsRedemptions";

/** Wire status -> screen label; `"Pending"` reads as `"Scheduled"`. */
export function redemptionStatusDisplay(status: CampaignLinkStatus): string {
  return status === CAMPAIGN_STATUSES.LINK_PENDING ? SCHEDULED_LABEL : status;
}

/** Screen label -> wire status; null when the label matches no known status. */
export function redemptionStatusWire(display: string): CampaignLinkStatus | null {
  if (display === SCHEDULED_LABEL) return "Pending";
  return (CAMPAIGN_LINK_STATUS_VALUES as readonly string[]).includes(display)
    ? (display as CampaignLinkStatus)
    : null;
}

/** Blotter query state -> API list params (chips -> campaign_id/status, daterange -> from/to). */
export function toListParams(q: {
  search: string;
  filters: AppliedFilter[];
  page: number;
  limit: number;
}): RedemptionsListParams {
  const params: RedemptionsListParams = {
    search: q.search.trim() || undefined,
    page: q.page,
    limit: q.limit,
  };

  for (const filter of q.filters) {
    switch (filter.fieldId) {
      case FILTER_FIELD_IDS.campaign:
        params.campaign_id = filter.values.length ? filter.values : undefined;
        break;
      case FILTER_FIELD_IDS.status: {
        const statuses = filter.values
          .map(redemptionStatusWire)
          .filter((status): status is CampaignLinkStatus => status !== null);
        params.status = statuses.length ? statuses : undefined;
        break;
      }
      case FILTER_FIELD_IDS.sent: {
        const [from, to] = filter.values;
        if (from) params.sentFrom = from;
        if (to) params.sentTo = to;
        break;
      }
      case FILTER_FIELD_IDS.redeemed: {
        const [from, to] = filter.values;
        if (from) params.redeemedFrom = from;
        if (to) params.redeemedTo = to;
        break;
      }
      default:
        break;
    }
  }

  return params;
}

/** CSV export query -> list params: search/filters plus sort, never page/limit. */
export function toRedemptionsExportParams(q: {
  search: string;
  filters: AppliedFilter[];
  sortBy: string;
  sortDir: "asc" | "desc";
}): RedemptionsListParams {
  const { page: _page, limit: _limit, ...params } = toListParams({ ...q, page: 1, limit: 1 });
  const sortBy = toSortBy(q.sortBy);
  return { ...params, ...(sortBy !== undefined ? { sortBy } : {}), sortDir: q.sortDir };
}

/** Column key -> sort key, discarding any key outside the whitelist. */
export function toSortBy(columnKey: string): RedemptionSortKey | undefined {
  if (columnKey === "name") return FIXED_COLUMN_SORT_KEY;
  return COLUMN_TO_SORT_KEY[columnKey as RedemptionColumnId] ?? undefined;
}

/** Null -> `EMPTY_CELL` for table cells; non-null passes through unchanged. */
export function orDash(value: string | null): string {
  return value !== null && value.trim() !== "" ? value : EMPTY_CELL;
}

/** Null/blank -> `""` for `sortValue` (empties sort together); non-null passes through unchanged. */
export function orBlank(value: string | null): string {
  return value !== null && value.trim() !== "" ? value : "";
}

/** Which empty state the panel shows: absolute empty, filtered-out empty, or a fetch error. */
export function pickEmptyMessage(args: {
  unfilteredTotal: number | undefined;
  isError: boolean;
}): "none-yet" | "no-match" | "error" {
  if (args.isError) return "error";
  if (args.unfilteredTotal === 0) return "none-yet";
  return "no-match";
}
